import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getShopifyStoreProducts, getShopifyVendorConfig, getCurrentUserRole } from '../services/shipturtleApi';
import styles from './ShipturtleProducts.module.css';
import '../components/ShipturtleVendors/VendorStoreManager.css'; // Reuse CSS

function ShipturtleVendorProductDetail() {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get('vendor_id');
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProduct = useCallback(async () => {
    if (!vendorId || !productId) {
      setError('Missing vendor ID or product ID.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // We don't have a "get single shopify product" API, so we fetch all and find it
      // Alternatively, we could add one, but for now we find it in the list
      const res = await getShopifyStoreProducts(vendorId);
      const found = res.products?.find(p => 
        String(p.vendor_shopify_product_id) === String(productId) ||
        String(p.shopify_product_id) === String(productId) || 
        String(p.internal_product_id) === String(productId) || 
        String(p.shopify_id) === String(productId) ||
        String(p.id) === String(productId)
      );
      
      if (found) {
        setProduct(found);
      } else {
        setError('Product not found in store.');
      }

      // Check admin status for action logic (keep roles check for future use if needed, but remove unused state)
      await Promise.all([
        getShopifyVendorConfig(),
        getCurrentUserRole().catch(() => ({ role: null }))
      ]);

    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Failed to load product details.');
    } finally {
      setLoading(false);
    }
  }, [vendorId, productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  if (loading) {
    return (
      <div className={styles.terminalPageRoot}>
        <div className="vstore-loading-state">
          <div className="spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={styles.terminalPageRoot}>
        <div className="vstore-error-state" style={{ marginTop: '40px', display: 'flex', justifyContent: 'center' }}>
           <div className="error-card" style={{ textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', maxWidth: '500px' }}>
             <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
             <h3 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '12px', borderBottom: 'none' }}>Oops! Product Not Found</h3>
             <p style={{ color: '#64748b', marginBottom: '24px', lineHeight: '1.5' }}>
               {error || "We couldn't find the details for this product. It might have been removed, or the ID is invalid/undefined."}
             </p>
             <button onClick={() => navigate(`/suspire-terminal/vendor-store?vendor_id=${vendorId}`)} className="vstore-back-btn" style={{ padding: '10px 24px', fontWeight: 'bold' }}>
               ← Back to Vendor Store
             </button>
           </div>
        </div>
      </div>
    );
  }


  return (
    <div className={styles.terminalPageRoot}>
      <div className={styles.content}>
        <div className="vstore-detail-page" style={{ paddingTop: '10px' }}>
          {/* Navigation */}
          <div style={{ marginBottom: '24px' }}>
            <button 
              onClick={() => navigate(`/suspire-terminal/vendor-store?vendor_id=${vendorId}`)} 
              className="vstore-back-btn" 
              style={{ padding: '8px 16px', fontSize: '0.9rem' }}
            >
              ← Back to Vendor Store
            </button>
          </div>

          {/* Header Action Bar */}
          <div className="vstore-detail-header" style={{ alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', paddingBottom: '24px', marginBottom: '32px' }}>
            <div className="vstore-detail-title-group" style={{ maxWidth: '100%' }}>
              <div className="vstore-detail-badges" style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                {product.mapping_status === 'mapped' && (
                  <span className="badge mapped" style={{ background: '#e6fffb', color: '#00bfae', border: '1px solid #00bfae', fontWeight: '700', padding: '4px 12px', fontSize: '11px', textTransform: 'uppercase', borderRadius: '12px' }}>✓ Mapped</span>
                )}
                {product.internal_product_status === 'live' && (
                   <span className="badge pending" style={{ background: '#fff7ed', color: '#f97316', border: '1px solid #fdba74', fontWeight: '700', padding: '4px 12px', fontSize: '11px', textTransform: 'lowercase', borderRadius: '12px' }}>live</span>
                )}
              </div>
              <h1 style={{ fontSize: '2.4rem', fontWeight: '700', color: '#1e293b', margin: 0, borderBottom: 'none' }}>{product.title}</h1>
            </div>
          </div>

          <div className="vstore-detail-layout" style={{ gridTemplateColumns: 'minmax(300px, 350px) 1fr', gap: '40px' }}>

            {/* Left Sidebar: Image & Technical Details */}
            <div className="vstore-detail-sidebar">
              <div className="vstore-detail-card main-image-card" style={{ padding: '0', overflow: 'hidden', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="detail-image-wrapper" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {product.image?.src || product.image_url || product.image ? (
                    <img src={product.image?.src || product.image_url || product.image} alt={product.title} style={{ maxWidth: '100%', maxHeight: '100%', padding: '20px', objectFit: 'contain' }} />
                  ) : (
                    <div className="no-image-placeholder">No Image Available</div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content: Description & Variants */}
            <div className="vstore-detail-main">
              <div className="vstore-detail-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1e293b', borderBottom: 'none', marginBottom: '24px', padding: 0 }}>Product Description</h3>
                <div className="vstore-detail-description" style={{ fontSize: '1.05rem', color: '#475569', lineHeight: '1.8' }}>
                  {product.description ? (
                    <div dangerouslySetInnerHTML={{ __html: product.description }} />
                  ) : (
                    <p className="no-description">No description provided for this product.</p>
                  )}
                </div>
              </div>

              <div className="vstore-detail-card" style={{ marginTop: '24px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="vstore-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 'none', marginBottom: '24px', padding: 0 }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Variants</h3>
                  <span className="count-pill" style={{ background: '#f1f5f9', color: '#64748b', padding: '4px 12px', borderRadius: '20px', fontWeight: '700', fontSize: '0.85rem' }}>{product.variants?.length || 0}</span>
                </div>
                <div className="vstore-variants-container">
                  {product.variants && product.variants.length > 0 ? (
                    <table className="vstore-variants-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>VARIANT NAME</th>
                          <th style={{ textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>SKU</th>
                          <th style={{ textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>PRICE</th>
                          <th style={{ textAlign: 'left', color: '#64748b', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>INVENTORY</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.variants.map((v, i) => (
                          <tr key={v.id || i}>
                            <td className="variant-title" style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', fontWeight: '600', color: '#1e293b' }}>{v.title || 'Default Title'}</td>
                            <td style={{ padding: '16px', borderBottom: '1px solid #f1f5f9' }}>
                              <code style={{ background: '#f8fafc', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', color: '#64748b', border: '1px solid #e2e8f0' }}>{v.sku || '—'}</code>
                            </td>
                            <td className="variant-price" style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', fontWeight: '700', color: '#1e293b' }}>{v.price ? `₹${v.price}` : '—'}</td>
                            <td className="variant-inventory" style={{ padding: '16px', borderBottom: '1px solid #f1f5f9', fontWeight: '600', color: '#475569' }}>
                              {v.inventory_quantity ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="empty-state">This product has no variants.</p>
                  )}
                </div>
              </div>

              {product.tags && (
                <div className="vstore-detail-card" style={{ marginTop: '24px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#1e293b', borderBottom: 'none', marginBottom: '20px', padding: 0 }}>Tags</h3>
                  <div className="vstore-tags-cloud" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',') : [])).map((tag, i) => (
                      <span key={i} className="vstore-tag-pill" style={{ background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '500' }}>{tag.trim ? tag.trim() : tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Shopify Product Data Diagnostic Section */}
          <div style={{ marginTop: '72px', padding: '48px 0', borderTop: '2px solid #f1f5f9' }}>
            
            <div className="vstore-detail-layout" style={{ gridTemplateColumns: '1fr 1.4fr', gap: '40px', display: 'grid' }}>
              
              {/* Card 1: Product info side */}
              <div className="vstore-detail-card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '40px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b', margin: 0 }}>Product Metadata</h3>
                  <span style={{ 
                    background: (product.vendor_shopify_status || '').toUpperCase() === 'ACTIVE' ? '#ecfdf5' : '#f8fafc', 
                    color: (product.vendor_shopify_status || '').toUpperCase() === 'ACTIVE' ? '#059669' : '#64748b',
                    padding: '8px 16px', borderRadius: '24px', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em', border: '1.5px solid currentColor'
                  }}>
                    {product.vendor_shopify_status || '—'}
                  </span>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px' }}>
                  {[
                    { label: 'Internal ID', value: product.internal_product_id || product.mapping_id },
                    { label: 'Merchant Shopify Status', value: product.suspire_shopify_status },
                    { label: 'Vendor Shopify Status', value: product.vendor_shopify_status },
                    { label: 'Product State', value: product.product_state },
                    { label: 'Terminal Status', value: product.terminal_status },
                    { label: 'Category', value: product.category_name || product.product_type },
                    { label: 'Vendor ID', value: product.vendor_shopify_product_id },
                  ].map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{item.label}</span>
                      <span style={{ color: '#1e293b', fontSize: '1rem', fontWeight: '600', wordBreak: 'break-all' }}>{item.value || '—'}</span>
                    </div>
                  ))}

                  <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '14px', borderTop: '1px solid #f1f5f9', paddingTop: '24px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Shopify Tags</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {Array.isArray(product.tags) && product.tags.length > 0 ? (
                        product.tags.map((tag, idx) => (
                          <span key={idx} style={{ background: '#f0fdfa', color: '#0f766e', padding: '6px 14px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '700', border: '1px solid #ccfbf1' }}>
                            {tag}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic' }}>—</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: Variants side */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {product.variants && product.variants.length > 0 ? (
                  product.variants.map((v, idx) => {
                    const vImage = product.images?.[idx] || product.image?.src || product.image || null;
                    
                    return (
                      <details key={idx} style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: '6px solid #00bfae', borderRadius: '20px', overflow: 'hidden', transition: 'all 0.2s ease', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                        <summary style={{ padding: '24px 32px', cursor: 'pointer', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <span style={{ fontWeight: '800', color: '#1e293b', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>{v.title || '—'}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                <code style={{ fontSize: '0.75rem', color: '#475569', background: '#f1f5f9', padding: '3px 10px', borderRadius: '6px', fontWeight: '700', letterSpacing: '0.04em', fontFamily: 'monospace' }}>{v.sku || '—'}</code>
                                <span style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '800', letterSpacing: '0.05em' }}>Vendor ID: {v.vendor_shopify_variant_id || '—'}</span>
                                <span style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: '800', letterSpacing: '0.05em', fontFamily: 'monospace' }}>Internal ID: {v.internal_variant_id || '—'}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                            <div style={{ textAlign: 'right' }}>
                               <span style={{ display: 'block', fontSize: '1.4rem', fontWeight: '800', color: '#004442' }}>₹{v.price || '—'}</span>
                               <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Price</span>
                            </div>
                            {vImage ? (
                              <img src={vImage} alt={v.title} style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '12px', border: '1px solid #f1f5f9', boxShadow: '0 4px 8px rgba(0,0,0,0.06)' }} />
                            ) : (
                               <div style={{ width: '64px', height: '64px', background: '#f8fafc', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px dashed #cbd5e1' }}>
                                 <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                               </div>
                            )}
                          </div>
                        </summary>
                        
                        <div style={{ padding: '32px 40px', background: '#fafbfc', borderTop: '1px solid #f1f5f9', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
                          {[
                            { label: 'HS Code', value: v.hs_code },
                            { label: 'Country', value: v.country_of_origin },
                            { label: 'Unit Cost', value: v.unit_cost ? `₹${v.unit_cost}` : null },
                            { label: 'Weight', value: (v.weight_kg !== null && v.weight_kg !== undefined) ? `${v.weight_kg} kg` : null },
                            { label: 'Stock', value: v.inventory_quantity ?? '0' },
                            { label: 'Compare Price', value: v.compare_price ? `₹${v.compare_price}` : null },
                            { label: 'Shopify Variant ID', value: v.shopify_variant_id }
                          ].map((field, fIdx) => (
                            <div key={fIdx} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <span style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{field.label}</span>
                              <span style={{ color: '#334155', fontSize: '1rem', fontWeight: '700' }}>{field.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    );
                  })
                ) : (
                  <div style={{ padding: '64px', textAlign: 'center', background: '#fff', border: '2.5px dashed #f1f5f9', borderRadius: '24px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '16px' }}>🔍</div>
                    <p style={{ color: '#94a3b8', fontWeight: '700', fontSize: '1rem' }}>No Shopify variants identified for this record.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShipturtleVendorProductDetail;
